import { Component, useEffect, useState, type ErrorInfo, type ReactNode } from 'react';
import { useRouteError, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

// Audit fe-rel-1 — until now there was NO error boundary anywhere, so a single
// render throw (a malformed guide payload making a Studio child throw, a lazy
// chunk that fails for a non-preloadError reason) unmounted the WHOLE React tree
// to a blank white screen with no message and no recovery — on a creation-centric
// SaaS that reads as data loss. Two layers:
//   • <ErrorBoundary> — a class boundary wrapped around AppShell's <Outlet/>,
//     reset on navigation, so an error in ONE page keeps the app shell + nav and
//     the user can click away to recover without a full reload.
//   • <RouteError> — the react-router errorElement backstop for errors thrown
//     above the Outlet boundary (e.g. in AppShell itself or a loader).

// A stale-deploy chunk 404 that surfaces here (rather than via main.tsx's
// `vite:preloadError` handler — e.g. a render-time dynamic import or a CSS
// preload failure) used to land the user on the generic "something went wrong"
// screen. Detect those and auto-recover with a guarded reload instead.
function isChunkLoadError(err: unknown): boolean {
  const e = err as { name?: string; message?: string } | null;
  const name = e?.name || '';
  const msg = String(e?.message || err || '').toLowerCase();
  return (
    name === 'ChunkLoadError' ||
    msg.includes('dynamically imported module') ||
    msg.includes('importing a module script failed') ||
    msg.includes('module script failed') ||
    msg.includes('unable to preload css') ||
    msg.includes("failed to fetch")
  );
}

// One-time guarded reload — shares the `ws:chunk-reload` window with main.tsx's
// preloadError handler so a genuinely-gone chunk (bad deploy / CDN purge) can't
// loop forever. Returns true if a reload was issued, false once exhausted.
function tryChunkReloadOnce(): boolean {
  const KEY = 'ws:chunk-reload';
  try {
    let st: { n: number; at: number } = { n: 0, at: 0 };
    try { st = { n: 0, at: 0, ...JSON.parse(sessionStorage.getItem(KEY) || '{}') }; } catch { /* fresh */ }
    const now = Date.now();
    if (!st.at || now - st.at > 60_000) st = { n: 0, at: 0 };
    if (st.n < 2) {
      sessionStorage.setItem(KEY, JSON.stringify({ n: st.n + 1, at: now }));
      window.location.reload();
      return true;
    }
  } catch { /* sessionStorage unavailable — fall through to the fallback UI */ }
  return false;
}

// Shown for the split second before an auto-recovery reload takes effect, so a
// stale-chunk error never flashes the scary "something went wrong" card.
function Reloading() {
  return (
    <div className="grid place-items-center p-8 min-h-[50vh]" role="status" aria-live="polite">
      <Loader2 className="animate-spin text-ws-ink-4" size={28} />
    </div>
  );
}

function Fallback({ onReload, onHome, detail }: { onReload: () => void; onHome?: () => void; detail?: string }) {
  const { t } = useTranslation('errors');
  return (
    <div className="grid place-items-center p-8 min-h-[50vh]">
      <div className="ws-tile max-w-md w-full text-center p-6">
        <h2 className="text-lg font-semibold text-ws-ink">{t('boundary.title')}</h2>
        <p className="mt-2 text-sm text-ws-ink-3">
          {t('boundary.message')}
        </p>
        {detail && (
          <pre className="mt-3 text-left text-[11px] text-ws-ink-4 bg-ws-surface-2 rounded-md p-2 overflow-auto max-h-28 whitespace-pre-wrap">
            {detail}
          </pre>
        )}
        <div className="mt-5 flex items-center justify-center gap-2">
          {onHome && (
            <button type="button" className="ws-btn-secondary text-sm" onClick={onHome}>
              {t('boundary.backToConsole')}
            </button>
          )}
          <button type="button" className="ws-btn-primary text-sm" onClick={onReload}>
            {t('boundary.reload')}
          </button>
        </div>
      </div>
    </div>
  );
}

interface Props { children: ReactNode; resetKey?: string }
interface State { error: Error | null; chunkReloading: boolean }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, chunkReloading: false };

  static getDerivedStateFromError(error: Error): State {
    // Optimistically render the spinner (not the error card) for stale-chunk
    // errors; componentDidCatch decides whether a reload is actually possible.
    return { error, chunkReloading: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Visibility — surface to the console (and any wired telemetry) without
    // throwing again. Kept best-effort so logging can never re-crash the UI.
    console.error('[ErrorBoundary]', error, info?.componentStack);
    if (isChunkLoadError(error)) {
      // Auto-recover a stale deploy; if the reload budget is exhausted, drop the
      // spinner and show the normal fallback (with its manual Refresh button).
      if (!tryChunkReloadOnce()) this.setState({ chunkReloading: false });
    }
  }

  componentDidUpdate(prev: Props) {
    // Reset when the caller changes resetKey (we pass location.pathname), so
    // navigating away from the broken page recovers without a full reload.
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null, chunkReloading: false });
    }
  }

  render() {
    if (this.state.chunkReloading) return <Reloading />;
    if (this.state.error) {
      return (
        <Fallback
          detail={import.meta.env.DEV ? String(this.state.error?.message || this.state.error) : undefined}
          onReload={() => window.location.reload()}
        />
      );
    }
    return this.props.children;
  }
}

// Route errorElement backstop (catches loader + render errors above the Outlet
// boundary). react-router v7 `errorElement`.
export function RouteError() {
  const error = useRouteError() as Error | undefined;
  const navigate = useNavigate();
  const chunk = isChunkLoadError(error);
  const [reloadExhausted, setReloadExhausted] = useState(false);

  useEffect(() => {
    if (chunk && !tryChunkReloadOnce()) setReloadExhausted(true);
  }, [chunk]);

  // Optimistically show the spinner for a chunk error while the reload fires;
  // only fall through to the error card once the reload budget is spent.
  if (chunk && !reloadExhausted) return <Reloading />;

  return (
    <Fallback
      detail={import.meta.env.DEV ? String((error as any)?.message || error || '') : undefined}
      onReload={() => window.location.reload()}
      onHome={() => { navigate('/'); }}
    />
  );
}
